import { DayScheduledDiscount } from "./dayScheduledDiscount";
import { GiftExpirationType } from "./giftExpirationType";
import { GiftRule } from "./giftRule";
import { GiftProduct } from "./giftProduct";
import { GiftProductType } from "./giftProductType";

export enum GiftFrequency {
  Daily = 'daily',
  Never = "never",
  Weekly = "weekly",
}

export class Gift {

  id: string | undefined;
  identify: string | undefined;
  name: string | undefined;
  description: string | undefined;
  productName: string | undefined;
  productDescription: string | undefined;
  productValue: number | undefined;
  productImagePath: string | undefined;
  productId: string | undefined;
  products: GiftProduct[] = [];
  chainId: string | undefined;
  createdByEmployeeId: string | undefined;
  startAt: string | undefined;
  endAt: string | undefined;
  frequency: GiftFrequency | undefined;
  limitQuantity: number | undefined;
  limitIntervalInHours: number | undefined;
  expirationType: GiftExpirationType | undefined;
  expirationInHours: number | undefined;
  expirationTime: string | undefined;
  createdAt: string | undefined;
  giftRules: any[] = [];
  dayScheduledGifts: DayScheduledDiscount[] = [];
  weekSchedual: DayScheduledDiscount[] = [];
  tagIds: Array<string> = [];
  placeIds: Array<string> = [];
  placeGifts: Array<{ placeId: string, rtiCode?: string | null, isEvoActive: boolean | null, isTeixeiraPinto: boolean | null }> = [];
  giftProductPlaces: Array<any> = [];
  startTime: Date | undefined;
  endTime: Date | undefined;
  isSchedule: boolean = false;
  hasFrequency: boolean = false;
  integrationPlaces: any[] = [];
  notificationCreation: any = undefined;
  notificationAchievement: any = undefined;

  static createGift(value: Partial<Gift> & { giftProducts?: any[]; giftMarketingCampaigns?: any[] }) {
    const gift: Partial<Gift> = {
      name: value.name,
      description: value.description,
      productName: value.productName,
      productDescription: value.productDescription,
      productValue: value.productValue,
      productImagePath: value.productImagePath,
      productId: value.productId,
      chainId: value.chainId,
      giftRules: (value.giftRules || [])?.map((giftRule) => GiftRule.createGiftRule(giftRule)),
      dayScheduledGifts: value.dayScheduledGifts,
      placeIds: value.placeIds,
      limitQuantity: value.limitQuantity,
      limitIntervalInHours: value.limitIntervalInHours,
      expirationType: value.expirationType,
      expirationInHours: value.expirationInHours,
      expirationTime: value.expirationTime,
      isSchedule: !!(value.startAt || value.endAt || value.frequency),
      hasFrequency: !!(value.frequency),
      startAt: value.startAt,
      endAt: value.endAt,
      frequency: value.frequency,
      tagIds: value.tagIds,
      integrationPlaces: (value.placeGifts || []).map(placeGift => ({ placeId: placeGift.placeId, rtiCode: placeGift.rtiCode, isEvoActive: placeGift.isEvoActive, isTeixeiraPinto: placeGift.isTeixeiraPinto })),
    };

    // Processar notificações do clone (giftMarketingCampaigns)
    if (value.giftMarketingCampaigns && value.giftMarketingCampaigns.length > 0) {
      value.giftMarketingCampaigns.forEach((campaign: any) => {
        if (campaign.type === 'creation') {
          // Notificação de criação: sempre Push, com sendNow ou sendAt
          gift.notificationCreation = {
            enabled: true,
            title: campaign.title,
            body: campaign.body,
            pushMessage: campaign.pushMessage,
            sendNow: campaign.sendAt ? false : true, // Se tem sendAt, sendNow = false
            sendAt: campaign.sendAt || undefined, // Incluir sendAt se existir
          };
        } else if (campaign.type === 'achievement') {
          // Notificação de conquista: sempre Push, tem delayMinutes
          gift.notificationAchievement = {
            enabled: true,
            title: campaign.title,
            body: campaign.body,
            pushMessage: campaign.pushMessage,
            delayMinutes: campaign.delayMinutes || 0,
          };
        }
      });
    }

    // Transform giftProducts from API to products for form
    if (value.giftProducts && value.giftProducts.length > 0) {
      gift.products = value.giftProducts.map((giftProduct: any) => {
        const product: any = {
          id: giftProduct.id,
          type: giftProduct.productType,
          integrationType: giftProduct.integrationType || null,
        };

        // Virtual product fields
        if (giftProduct.productType === 'virtual') {
          product.productName = giftProduct.productName;
          product.productDescription = giftProduct.productDescription;
          product.productValue = giftProduct.productValue;
          product.productImagePath = giftProduct.productImagePath;
          product.discountPercentage = giftProduct.discountPercentage;
          product.cashValue = giftProduct.cashValue ?? null;
          // Produtos de Venda: o clone precisa levar junto, senão o brinde novo nasce sem
          // código de PDV e o AutoSystem recusa todo cupom dele.
          product.saleProductIds = giftProduct.saleProductIds;
        }

        // Existing product fields
        if (giftProduct.productType === 'existing') {
          product.productId = giftProduct.productId;
          product.productName = giftProduct.productName;
          product.productValue = giftProduct.productValue;
          product.productImagePath = giftProduct.productImagePath;
        }

        // Fuel discount - link to existing Discount
        if (giftProduct.productType === GiftProductType.FuelDiscount) {
          product.discountId = giftProduct.discountId;
        }

        // Recompensas-ponteiro: o clone precisa levar o vínculo junto, senão o
        // brinde novo nasce apontando para lugar nenhum e o wizard obriga a
        // escolher tudo de novo (defeito antigo do bolão e do sorteio).
        if (giftProduct.productType === GiftProductType.PredictionTicket) {
          product.bolaoId = giftProduct.bolaoId;
          product.bolaoName = giftProduct.bolaoName;
        }

        if (giftProduct.productType === GiftProductType.DrawingNumber) {
          product.drawingId = giftProduct.drawingId;
          product.drawingName = giftProduct.drawingName;
          product.luckyNumberAmount = giftProduct.luckyNumberAmount;
        }

        if (giftProduct.productType === GiftProductType.SpinTicket) {
          product.roletaId = giftProduct.roletaId;
          product.roletaName = giftProduct.roletaName;
          product.spinAmount = giftProduct.spinAmount;
        }

        return product;
      });
    } else if (value.products) {
      // If products already exist in correct format, use them
      gift.products = value.products;
    }

    // Transform giftProductPlaces structure for editing
    // The API returns giftProducts[].giftProductPlaces[]
    // We need to transform to giftProductPlaces[].places[] structure (product-centric)
    if (value.giftProducts) {
      gift.giftProductPlaces = value.giftProducts.map((giftProduct: any, productIndex: number) => {
        return {
          productIndex: productIndex,
          productId: giftProduct.productId || null,
          places: (giftProduct.giftProductPlaces || []).map((placeConfig: any) => ({
            placeId: placeConfig.placeId,
            partnerValue: placeConfig.partnerValue,
            hasProductPlace: placeConfig.hasProductPlace,
            rtiCode: placeConfig.rtiCode,
            isEvoActive: placeConfig.isEvoActive,
            discountPercentage: placeConfig.discountPercentage,
          }))
        };
      });
    } else if (value.giftProductPlaces) {
      // If giftProductPlaces already exist in correct format, use them
      gift.giftProductPlaces = value.giftProductPlaces;
    }

    if (value.frequency === GiftFrequency.Daily) {
      gift.startTime = value.dayScheduledGifts && value.dayScheduledGifts[0].startTime;
      gift.endTime = value.dayScheduledGifts && value.dayScheduledGifts[0].endTime;
    }

    return gift;
  }

  constructor(init?: Partial<Gift>) {
    Object.assign(this, init);
  }
}